import React, { FC, useCallback } from "react";
import { useCurrencySymbol } from "hooks/useCurrencySymbol";
import mpsIcon from "components/Icon/icons/mps_icon.gif";
import { Map } from "immutable";
import { Hidden, Visible } from "react-grid-system";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import { OutgoingComponentCommonProps } from "types/outgoing";
import PackageSizes from "../../../../components/PackageSizes/PackageSizes";
import ServiceLogo from "../../../../components/ServiceLogo/ServiceLogo";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import {
  panelSelectData,
  panelSelectOpened,
} from "../../../../reduxFolder/selectors/rightPanelSelectors";
import {
  ExcessMarker,
  OutgoingEndCol,
  OutgoingGrowCol,
  OutgoingGrowText,
  OutgoingMainCol,
  OutgoingMetaCol,
} from "../../../../styles/outgoingStyles";
import { Currency, OutgoingParcelRow } from "../../../../styles/parcelsStyles";
import formatter from "../../../../utils/formatter";
import {
  DIMENSIONS_COL_WIDTH,
  PRICE_COL_WIDTH,
  SKU_COL_WIDTH,
  WEIGHT_COL_WIDTH,
} from "../rowLayout";
import { MPSIcon, OutgoingCol, Price, Weight } from "./Paid.styles";

const Paid: FC<OutgoingComponentCommonProps> = React.memo(
  ({
    openRightPanel,
    parcel,
    isPanelOpened,
    panelData,
    units,
    layoutTier = "full",
  }) => {
    const { t } = useTranslation("common");
    const currencySymbol = useCurrencySymbol();
    const deliveryName = parcel.get("preferred_carrier");
    const isSelectedShipment =
      isPanelOpened &&
      panelData &&
      panelData.get("detailedItemID") === parcel.get("id");
    const hasFewPackages = (parcel.get("package_count") || 0) > 1;
    const hasExcessItems = parcel.get("excess_items").size > 0;
    const showWeightCol = layoutTier !== "narrow";
    const showDimsCol = layoutTier === "full";

    const clickParcel = useCallback(
      () =>
        openRightPanel(
          "IN_PROGRESS",
          Map({
            detailedItemID: parcel.get("id"),
            excessItems: parcel.get("excess_items"),
            returnPreviousPanel: "IN_PROGRESS",
          }),
        ),
      [openRightPanel, parcel],
    );

    const getWeight = () => {
      let weight = parcel.get("total_weight");
      const unit = t(`units.${units}.primaryShort`);

      return `${weight} ${unit}`;
    };

    const getPackageDimmensions = () => {
      if (parcel.get("package_count") === 1) {
        return (
          <PackageSizes
            width={parcel.get("width")}
            height={parcel.get("height")}
            depth={parcel.get("depth")}
            units={units}
          />
        );
      } else {
        const package_count = parcel.get("package_count");
        const countId = package_count <= 10 ? package_count : "nth";
        return t(`conjugation.packages.${countId}`, { count: package_count });
      }
    };

    return (
      <OutgoingParcelRow selected={isSelectedShipment} onClick={clickParcel}>
        <Visible xs>
          <OutgoingCol xs={3}>
            {parcel.get("sku")}
            {hasExcessItems ? <ExcessMarker /> : null}
            {hasFewPackages ? <MPSIcon icon={mpsIcon} size={[14, 14]} /> : null}
          </OutgoingCol>
          <OutgoingCol xs={5}>
            <ServiceLogo serviceKey={deliveryName} />
            {getPackageDimmensions()}
          </OutgoingCol>
          <OutgoingCol xs={4}>
            <Price>
              <Currency>{currencySymbol}</Currency>{" "}
              {formatter.currency(parcel.getIn(["estimate", "total"]))}
              <Weight>{` / ${getWeight()}`}</Weight>
            </Price>
          </OutgoingCol>
        </Visible>
        <Hidden xs>
          <OutgoingMainCol sm={12}>
            <OutgoingMetaCol $width={SKU_COL_WIDTH} $grow={0}>
              {parcel.get("sku")}
              {hasExcessItems ? <ExcessMarker /> : null}
            </OutgoingMetaCol>
            <OutgoingGrowCol>
              <ServiceLogo serviceKey={deliveryName} />
              <OutgoingGrowText>{deliveryName}</OutgoingGrowText>
            </OutgoingGrowCol>
            {showWeightCol && (
              <OutgoingMetaCol $width={WEIGHT_COL_WIDTH}>
                {getWeight()}
              </OutgoingMetaCol>
            )}
            {showDimsCol && (
              <OutgoingMetaCol $width={DIMENSIONS_COL_WIDTH} $grow={1.5}>
                {getPackageDimmensions()}
              </OutgoingMetaCol>
            )}
            <OutgoingEndCol $width={PRICE_COL_WIDTH}>
              <Price>
                <Currency>{currencySymbol}</Currency>{" "}
                {formatter.currency(parcel.getIn(["estimate", "total"]))}
              </Price>
            </OutgoingEndCol>
          </OutgoingMainCol>
        </Hidden>
      </OutgoingParcelRow>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    isPanelOpened: panelSelectOpened,
    panelData: panelSelectData,
  }),
  { openRightPanel },
);

export default withConnect(Paid);
